import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { combineAbortSignals } from '#pplx/frontend/fetcher/combineAbortSignals.js';
import { APP_DOMAIN_HEADER, REASON_HEADER, REQUEST_ID_HEADER, SERVER_SOURCE, SSE_TERMINAL_HEADER, WEB_SOURCE, WEB_VERSION, } from '#pplx/frontend/fetcher/constants.js';
import { FetcherError } from '#pplx/frontend/fetcher/errors.js';
import { isStreamTerminal } from '#pplx/frontend/fetcher/terminalStream.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { startTimer } from '#pplx/frontend/metrics/browser.js';
import { EventSource } from 'eventsource';
const NORMALIZED_PATHS = new WeakMap();
const TimeoutErrorName = 'TimeoutError';
const MAX_RETRIES = 2;
const MAX_BACKOFF_EXPONENTIAL_MS = 64_000;
const SERVER_DEFAULT_TIMEOUT_MS = 1250;
const CLIENT_DEFAULT_READ_TIMEOUT_MS = 10000; // Golang GET timeout
const CLIENT_DEFAULT_WRITE_TIMEOUT_MS = 15000; // Golang POST timeout
const REDACTED_RESOURCE_BASE_URL = 'https://redacted.invalid';
async function _readTerminalErrorCode(response, terminalErrorCodes) {
    if (!terminalErrorCodes?.length ||
        response.status !== 403 ||
        response.headers.get('cf-mitigated')?.includes('challenge') ||
        response.headers.get('Content-Type')?.split(';')[0]?.trim() !== 'application/json') {
        return undefined;
    }
    try {
        const body = (await response.clone().json());
        const code = body?.detail?.error_code;
        return typeof code === 'string' && terminalErrorCodes.includes(code) ? code : undefined;
    }
    catch {
        return undefined;
    }
}
function getFetchResourceForDiagnostics(resource, normalizedPath) {
    if (normalizedPath) {
        return normalizedPath.split(/[?#]/, 1)[0] ?? normalizedPath;
    }
    const resourceUrl = typeof resource === 'string' || resource instanceof URL ? String(resource) : resource.url;
    try {
        return new URL(resourceUrl, REDACTED_RESOURCE_BASE_URL).pathname;
    }
    catch {
        return '[invalid fetch resource]';
    }
}
export const _addSourceVersionQueryParams = (fullUrl) => {
    const isServer = typeof window === 'undefined';
    const clonedUrl = new URL(fullUrl);
    const enhancedSearchURLParams = clonedUrl.searchParams;
    if (!enhancedSearchURLParams.get('version')) {
        enhancedSearchURLParams.set('version', WEB_VERSION);
    }
    if (!enhancedSearchURLParams.get('source')) {
        enhancedSearchURLParams.set('source', isServer ? SERVER_SOURCE : WEB_SOURCE);
    }
    return clonedUrl;
};
/**
 * Returns a timeout that is adaptive to the user's connection type and speed
 * PRIVATE API, export for testing only
 * @param timeoutMs - The timeout in milliseconds
 * @returns The adaptive timeout in milliseconds
 */
export function _getAdaptiveTimeout(timeoutMs, method) {
    const defaultTimeoutMs = Math.max(typeof window !== 'undefined'
        ? method === 'GET'
            ? CLIENT_DEFAULT_READ_TIMEOUT_MS
            : CLIENT_DEFAULT_WRITE_TIMEOUT_MS
        : SERVER_DEFAULT_TIMEOUT_MS, timeoutMs);
    try {
        // This mostly comes from https://github.com/ppl-ai/agi/blob/a01ab7a095e7bf4d8201f32ebd479238b8e9221d/pplx/frontend/packages/components/src/utils/userAgentUtils.ts
        const isMobile = globalThis.navigator.userAgent.includes('Mobile') &&
            !globalThis.navigator.userAgent.includes('iPad');
        let type = 'unknown';
        // Default to 10Mbps so it's basically wifi
        let downlink = 10;
        if (globalThis.navigator.connection) {
            type = globalThis.navigator.connection.type;
            downlink = globalThis.navigator.connection.downlink;
        }
        // We're being more lenient on mobile devices and slow connections
        if (isMobile || downlink < 1 || type === 'cellular') {
            return defaultTimeoutMs * 2;
        }
        return defaultTimeoutMs;
    }
    catch {
        return defaultTimeoutMs;
    }
}
/**
 * Returns true if the response status is suitable for retrying
 */
const _isRetryableStatus = (status) => {
    switch (status) {
        // Our Python Client Error Codes we CANNOT retry
        case 400: // Bad Request - Used for invalid request parameters or malformed requests
        case 403: // Forbidden - Used when access is denied
        case 404: // Not Found - Used when requested resource doesn't exist
            return false;
        // Our Python Client Error Codes we CAN retry
        /**
         * 401 Unauthorized - This can happen if we cannot look up the user in our
         * backend DB, possibly due to a read replica delay or just the DB being
         * unresponsive.
         */
        case 401:
        case 409: // Conflict - Used for resource conflicts (e.g., duplicate entries)
            return true;
        // Our Python Server Error Codes we CAN retry
        case 500: // Internal Server Error - Used for unexpected server errors
        case 502: // Bad Gateway - Used for unexpected server errors
        case 429: // Too Many Requests - Used for rate limiting
            return true;
        default:
            return false;
    }
};
/**
 * Returns true if the EventSource handshake status should be retried.
 * Per the EventSource spec, non-200 HTTP responses fail the connection.
 * We intentionally override that baseline for a small set of backend
 * statuses that are plausibly transient during the SSE handshake.
 * PRIVATE API, export for testing only
 */
export const _isRetryableSseStatus = (status) => {
    switch (status) {
        case 429: // Too Many Requests - Used for rate limiting
        case 503: // Service Unavailable - Used for load shedding
            return true;
        default:
            return false;
    }
};
/**
 * Returns the retry after time in milliseconds
 * PRIVATE API, export for testing only
 */
export const _getRetryAfter = (retryAfter) => {
    if (!retryAfter) {
        return 0;
    }
    const retryAfterSeconds = parseInt(retryAfter, 10);
    if (!isNaN(retryAfterSeconds)) {
        return Math.max(0, retryAfterSeconds * 1e3);
    }
    const retryAfterDate = new Date(retryAfter);
    const retryAfterTime = retryAfterDate.getTime();
    if (isNaN(retryAfterTime)) {
        return 0;
    }
    return Math.max(0, retryAfterTime - Date.now());
};
/**
 * Returns the appropriate backoff time for a given try attempt
 * and retry after if applicable
 * PRIVATE API, export for testing only
 */
export const _getBackoffTime = (backoffTime, tryNumber, retryAfter) => {
    const cappedBackoff = Math.min(backoffTime * Math.pow(2, tryNumber), MAX_BACKOFF_EXPONENTIAL_MS);
    const jitter = Math.random() * 0.3 + 0.85; // +/- 15%
    return retryAfter + cappedBackoff * jitter;
};
const createCallerAbortError = () => new FetcherError('FETCHER_NO_STATUS_CODE_ERROR', {
    message: 'The operation was aborted',
});
const waitForRetry = (delayMs, signal) => new Promise((resolve) => {
    if (!signal) {
        setTimeout(resolve, delayMs);
    }
    else if (signal.aborted) {
        resolve();
    }
    else {
        combineAbortSignals(signal, AbortSignal.timeout(delayMs)).addEventListener('abort', () => resolve());
    }
});
export const fetchWithTimeout = async ({ resource, options = {}, method = 'GET', timeoutMs = typeof window !== 'undefined'
    ? method === 'GET'
        ? CLIENT_DEFAULT_READ_TIMEOUT_MS
        : CLIENT_DEFAULT_WRITE_TIMEOUT_MS
    : SERVER_DEFAULT_TIMEOUT_MS, body, signal, numRetries = 0, backOffTime = 1000, normalizedPath, reason, appDomain, }) => {
    const diagnosticResource = getFetchResourceForDiagnostics(resource, normalizedPath);
    const fetchOptions = {
        ...options,
        method,
        body,
        signal,
    };
    const headers = new Headers(fetchOptions.headers);
    headers.set(REASON_HEADER, reason);
    if (!headers.has(REQUEST_ID_HEADER)) {
        headers.set(REQUEST_ID_HEADER, safeRandomUUID());
    }
    if (appDomain) {
        headers.set(APP_DOMAIN_HEADER, appDomain);
    }
    if (appDomain === 'api-console') {
        headers.set('X-App-ApiClient', appDomain);
    }
    let startTimeout = () => {
        return null;
    };
    if (timeoutMs) {
        const normalizedTimeoutMs = _getAdaptiveTimeout(timeoutMs, method);
        // @ts-expect-error TS(2322): Type '() => ReturnType<typeof setTimeout>' is not assignable to t... Remove this comment to see the full error message
        startTimeout = () => {
            const reason = new DOMException(`fetch ${diagnosticResource} timed out (${normalizedTimeoutMs}ms)`, TimeoutErrorName);
            const controller = new AbortController();
            fetchOptions.signal = signal
                ? combineAbortSignals(signal, controller.signal)
                : controller.signal;
            return setTimeout(() => controller.abort(reason), normalizedTimeoutMs);
        };
    }
    if (numRetries > MAX_RETRIES) {
        const error = new FetcherError('FETCHER_MAX_RETRIES_EXCEEDED', {
            details: {
                resource: diagnosticResource,
                numRetries,
                maxRetries: MAX_RETRIES,
            },
        });
        if (process.env.NODE_ENV === 'development') {
            throw error;
        }
        else {
            logger.error(error);
        }
    }
    // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'Response'.
    let response = null;
    let exception;
    let sleepTime = backOffTime;
    let retryAfter = 0;
    let tryNumber = 0;
    do {
        // Backoff, but only if we're retrying
        if (numRetries > 0 && tryNumber > 0) {
            await waitForRetry(sleepTime, signal);
            if (signal?.aborted)
                throw createCallerAbortError();
        }
        const timeout = startTimeout();
        retryAfter = 0;
        exception = undefined;
        try {
            headers.set('X-Perplexity-Request-Try-Number', String(tryNumber + 1));
            headers.set('X-Perplexity-Request-Endpoint', String(resource));
            response = await fetch(resource, {
                ...fetchOptions,
                headers,
            });
            if (!response.ok && _isRetryableStatus(response.status)) {
                retryAfter = _getRetryAfter(response.headers.get('Retry-After'));
            }
            else {
                break;
            }
        }
        catch (err) {
            // Handles network errors and timeout abort errors
            exception = new FetcherError('FETCHER_NO_STATUS_CODE_ERROR', {
                message: err?.message,
                cause: err?.cause,
            });
        }
        finally {
            // @ts-expect-error No overload matches this call.
            clearTimeout(timeout);
        }
        if (signal?.aborted)
            throw createCallerAbortError();
        sleepTime = _getBackoffTime(backOffTime, tryNumber, retryAfter);
        ++tryNumber;
    } while (tryNumber <= numRetries);
    try {
        if (response)
            NORMALIZED_PATHS.set(response, normalizedPath);
    }
    catch {
        // Ignore
    }
    if (!exception && !response.ok) {
        if (response.status === 403 && response.headers.get('cf-mitigated')?.includes('challenge')) {
            exception = new FetcherError('FETCHER_CLOUDFLARE_403_ERROR');
        }
        else {
            const html = (await response.clone().text()).trimStart();
            if (html.startsWith('<!DOCTYPE html') || html.startsWith('<html'))
                exception = new FetcherError('FETCHER_HTML_STATUS_CODE_ERROR');
        }
    }
    if (exception) {
        logger.log(`${fetchOptions.method} ${diagnosticResource} exception`, exception);
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        exception._fetchMethod = fetchOptions.method;
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        exception._fetchResource = diagnosticResource;
        throw exception;
    }
    return response;
};
/**
 * Timeout before the SSE connection opens and between SSE messages: 10 minutes
 */
const SSE_TIMEOUT_MS = 10 * 60 * 1e3;
const CONNECTION_TIMEOUT_MS = SSE_TIMEOUT_MS;
const HEARTBEAT_TIMEOUT_MS = SSE_TIMEOUT_MS;
/**
 * Stream data from an SSE endpoint. Resolves when the end_of_stream message is received. Rejects on error or timeout.
 * @param opts options
 * @returns Promise that contains the final message
 */
export function sseStream({ url, normalizedPath, method = 'POST', params, queryParams, handlers, legacyEvents = {}, headers = {}, signal, numRetries = 0, terminalErrorCodes, backOffTime = 1000, metrics = {}, timer, }) {
    const messageEvent = legacyEvents.message ?? 'message';
    const endOfStreamEvent = legacyEvents.endOfStream ?? 'end_of_stream';
    const isLegacy = legacyEvents.message && legacyEvents.endOfStream;
    const { now, ...restMetrics } = metrics;
    if (!timer) {
        timer = startTimer({
            normalizedPath,
            ...restMetrics,
        }, { nowFromTimeOrigin: performance.now() });
    }
    else {
        timer.setGlobalContext({
            normalizedPath,
            ...restMetrics,
        });
    }
    const { addTiming, addTimingOnce } = timer;
    addTiming('web.sse.start');
    // Infinity is the explicit polling signal; bounded retry counts still go
    // through the normal MAX_RETRIES guard below.
    const shouldPoll = numRetries === Infinity;
    const hasBoundedRetries = Number.isFinite(numRetries);
    let currentEventSource;
    let releaseWait;
    let backoffTimer;
    let isClosedByCaller = false;
    let lastEventId;
    const close = () => {
        isClosedByCaller = true;
        clearTimeout(backoffTimer);
        // Resolving releaseWait tears down either the current backoff sleep or the
        // active attempt. The attempt-side resolveOnce guard keeps shutdown idempotent
        // if EventSource.close() also surfaces an error event on the way out.
        releaseWait?.();
        try {
            currentEventSource?.close();
        }
        catch {
            // Ignore
        }
    };
    const streamPromise = (async () => {
        if (hasBoundedRetries && numRetries > MAX_RETRIES) {
            const error = new FetcherError('FETCHER_MAX_RETRIES_EXCEEDED', {
                details: {
                    resource: url.toString(),
                    numRetries,
                    maxRetries: MAX_RETRIES,
                },
            });
            if (process.env.NODE_ENV === 'development') {
                throw error;
            }
            else {
                logger.error(error);
            }
        }
        let exception;
        let sleepTime = backOffTime;
        let retryAfter = 0;
        let tryNumber = 0;
        do {
            if (isClosedByCaller || signal?.aborted) {
                return;
            }
            if (tryNumber > 0) {
                await new Promise((resolve) => {
                    let settled = false;
                    const finish = () => {
                        if (settled) {
                            return;
                        }
                        settled = true;
                        signal?.removeEventListener('abort', finish);
                        clearTimeout(backoffTimer);
                        backoffTimer = undefined;
                        releaseWait = undefined;
                        resolve();
                    };
                    releaseWait = finish;
                    backoffTimer = setTimeout(finish, sleepTime);
                    signal?.addEventListener('abort', finish, { once: true });
                });
            }
            if (isClosedByCaller || signal?.aborted) {
                return;
            }
            const attempt = await new Promise((resolve) => {
                let attemptRetryAfter = 0;
                let attemptOpened = false;
                let attemptTerminal = false;
                let backendErrorCode;
                let attemptBody = null;
                let attemptCfChallenge = false;
                let settled = false;
                let connectionTimer;
                let heartbeatTimer;
                let chunkTimer;
                const stopConnectionTimer = () => {
                    clearTimeout(connectionTimer);
                    connectionTimer = undefined;
                };
                const stopHeartbeat = () => {
                    clearTimeout(heartbeatTimer);
                    heartbeatTimer = undefined;
                };
                const abortHandler = () => {
                    closeEventSource();
                    resolveOnce({
                        retryAfter: attemptRetryAfter,
                        shouldRetry: false,
                        opened: attemptOpened,
                    });
                };
                const resolveOnce = (result) => {
                    if (settled) {
                        return;
                    }
                    settled = true;
                    stopConnectionTimer();
                    stopHeartbeat();
                    signal?.removeEventListener('abort', abortHandler);
                    currentEventSource = undefined;
                    releaseWait = undefined;
                    eventSource.removeEventListener('error', errorHandler);
                    eventSource.removeEventListener('open', openHandler);
                    eventSource.removeEventListener(messageEvent, messageHandler);
                    eventSource.removeEventListener(endOfStreamEvent, endOfStreamHandler);
                    eventSource.removeEventListener('info', infoHandler);
                    resolve(result);
                };
                const closeEventSource = () => {
                    try {
                        eventSource.close();
                    }
                    catch {
                        // Ignore
                    }
                };
                const handleStreamError = (error, shouldRetry) => {
                    closeEventSource();
                    if (error instanceof FetcherError) {
                        resolveOnce({
                            error,
                            retryAfter: attemptRetryAfter,
                            shouldRetry,
                            opened: attemptOpened,
                            terminal: attemptTerminal,
                        });
                        return;
                    }
                    resolveOnce({
                        error: new FetcherError('FETCHER_SSE_ERROR', {
                            message: 'An error occurred while streaming',
                        }),
                        retryAfter: attemptRetryAfter,
                        shouldRetry,
                        opened: attemptOpened,
                        terminal: attemptTerminal,
                    });
                };
                function resetChunkTimer() {
                    if (!chunkTimer) {
                        chunkTimer = startTimer({
                            normalizedPath,
                            ...restMetrics,
                        });
                    }
                    else {
                        chunkTimer = undefined;
                    }
                }
                function startConnectionTimer() {
                    stopConnectionTimer();
                    connectionTimer = setTimeout(() => {
                        handleStreamError(new FetcherError('FETCHER_SSE_CONNECTION_TIMEOUT'), true);
                    }, CONNECTION_TIMEOUT_MS);
                }
                function resetHeartbeat() {
                    stopHeartbeat();
                    heartbeatTimer = setTimeout(() => {
                        handleStreamError(new FetcherError('FETCHER_SSE_CHUNK_TIMEOUT'), true);
                    }, HEARTBEAT_TIMEOUT_MS);
                }
                const errorHandler = (e) => {
                    if (isClosedByCaller || signal?.aborted) {
                        closeEventSource();
                        resolveOnce({
                            retryAfter: attemptRetryAfter,
                            shouldRetry: false,
                            opened: attemptOpened,
                        });
                        return;
                    }
                    let errorCode = 'FETCHER_SSE_ERROR';
                    let shouldRetry = false;
                    attemptTerminal ||= isStreamTerminal(attemptBody);
                    if (backendErrorCode) {
                        errorCode = 'FETCHER_SSE_APPLICATION_ERROR';
                    }
                    else if (attemptTerminal) {
                        errorCode = 'FETCHER_SSE_TERMINAL_STATUS_ERROR';
                    }
                    else if (!navigator.onLine) {
                        errorCode = 'FETCHER_SSE_OFFLINE_ERROR';
                        shouldRetry = true;
                    }
                    else if (e.message === 'network error') {
                        errorCode = 'FETCHER_SSE_NETWORK_ERROR';
                        shouldRetry = true;
                    }
                    else if (!e.code) {
                        errorCode = 'FETCHER_SSE_NO_STATUS_CODE_ERROR';
                        shouldRetry = true;
                    }
                    else if (e.code === 403 && attemptCfChallenge) {
                        errorCode = 'FETCHER_SSE_CLOUDFLARE_403_ERROR';
                    }
                    else if (e.code === 403 || e.code === 404 || e.code === 410) {
                        // The resource is gone or not accessible; callers must not auto-reconnect.
                        errorCode = 'FETCHER_SSE_TERMINAL_STATUS_ERROR';
                    }
                    else {
                        shouldRetry = _isRetryableSseStatus(e.code);
                    }
                    handleStreamError(new FetcherError(errorCode, {
                        cause: e,
                        backendErrorCode,
                        details: {
                            backendErrorCode,
                            code: errorCode,
                            message: e.message,
                            type: e.type,
                        },
                    }), shouldRetry);
                };
                const openHandler = () => {
                    stopConnectionTimer();
                    resetHeartbeat();
                    attemptOpened = true;
                    addTiming('web.sse.connection_opened');
                    handlers.open?.();
                };
                const messageHandler = (m) => {
                    let data;
                    if (m.lastEventId) {
                        lastEventId = m.lastEventId;
                    }
                    try {
                        addTimingOnce('web.sse.first_message_seen');
                        resetChunkTimer();
                        resetHeartbeat();
                        data = JSON.parse(m.data);
                    }
                    catch {
                        return handleStreamError(new FetcherError('FETCHER_SSE_CHUNK_PROCESSING_ERROR'), false);
                    }
                    handlers.message(data);
                };
                const endOfStreamHandler = (m) => {
                    let data;
                    try {
                        addTimingOnce('web.sse.first_message_seen');
                        resetChunkTimer();
                        stopHeartbeat();
                        closeEventSource();
                        data = JSON.parse(m.data);
                    }
                    catch {
                        return handleStreamError(new FetcherError('FETCHER_SSE_EOS_ERROR'), false);
                    }
                    if (isLegacy) {
                        handlers.message(data);
                    }
                    addTiming('web.sse.end_of_stream');
                    resolveOnce({
                        retryAfter: attemptRetryAfter,
                        shouldRetry: false,
                        opened: attemptOpened,
                    });
                };
                const infoHandler = (m) => {
                    let data;
                    try {
                        data = JSON.parse(m.data);
                    }
                    catch {
                        return handleStreamError(new FetcherError('FETCHER_SSE_INFO_CHUNK_PARSE_ERROR'), false);
                    }
                    handlers.info?.(data);
                };
                const pingHandler = () => {
                    resetHeartbeat();
                };
                const eventSource = new EventSource(url, {
                    withCredentials: true,
                    fetch: (_input, init) => {
                        const requestUrl = new URL(url);
                        const attemptHeaders = new Headers(init?.headers);
                        Object.entries(headers).forEach(([key, value]) => {
                            attemptHeaders.set(key, value);
                        });
                        // The eventsource library sets Last-Event-ID for its own
                        // same-instance reconnects; this fills the cross-attempt gap so
                        // servers that stamp ids can replay events missed while
                        // disconnected. Streams without ids are unaffected.
                        if (lastEventId && !attemptHeaders.has('Last-Event-ID')) {
                            attemptHeaders.set('Last-Event-ID', lastEventId);
                        }
                        attemptHeaders.set('X-Perplexity-Request-Try-Number', String(tryNumber + 1));
                        attemptHeaders.set('X-Perplexity-Request-Endpoint', url.toString());
                        // The eventsource library passes its own internal AbortController
                        // signal via init.signal — aborting it is how eventSource.close()
                        // tears down the underlying fetch.  We must preserve that signal;
                        // if the caller also provided one we combine both so either side
                        // can cancel the request.
                        const combinedSignal = signal && init?.signal
                            ? combineAbortSignals(init.signal, signal)
                            : (signal ?? init?.signal);
                        const fetchParams = {
                            ...init,
                            method,
                            credentials: 'include',
                            signal: combinedSignal,
                            headers: attemptHeaders,
                        };
                        if (method === 'GET') {
                            for (const [key, value] of Object.entries(queryParams ?? {})) {
                                if (value === undefined || value === null) {
                                    continue;
                                }
                                requestUrl.searchParams.set(key, String(value));
                            }
                        }
                        else if (method === 'POST') {
                            fetchParams.body = params ? JSON.stringify(params) : undefined;
                            attemptHeaders.set('content-type', 'application/json');
                        }
                        return fetch(requestUrl, fetchParams).then(async (response) => {
                            try {
                                if (response)
                                    NORMALIZED_PATHS.set(response, normalizedPath);
                            }
                            catch {
                                // Ignore
                            }
                            if (!response.ok && _isRetryableSseStatus(response.status)) {
                                attemptRetryAfter = _getRetryAfter(response.headers.get('Retry-After'));
                            }
                            attemptTerminal = response.headers.has(SSE_TERMINAL_HEADER);
                            attemptCfChallenge =
                                response.status === 403 &&
                                    (response.headers.get('cf-mitigated')?.includes('challenge') ?? false);
                            attemptBody = response.body;
                            backendErrorCode = await _readTerminalErrorCode(response, terminalErrorCodes);
                            if (backendErrorCode)
                                attemptTerminal = true;
                            return response;
                        });
                    },
                });
                currentEventSource = eventSource;
                releaseWait = () => {
                    closeEventSource();
                    resolveOnce({
                        retryAfter: attemptRetryAfter,
                        shouldRetry: false,
                        opened: attemptOpened,
                    });
                };
                eventSource.addEventListener('error', errorHandler);
                eventSource.addEventListener('open', openHandler);
                eventSource.addEventListener(messageEvent, messageHandler);
                eventSource.addEventListener(endOfStreamEvent, endOfStreamHandler);
                eventSource.addEventListener('info', infoHandler);
                eventSource.addEventListener('ping', pingHandler);
                signal?.addEventListener('abort', abortHandler, { once: true });
                startConnectionTimer();
            });
            if (!attempt.error) {
                return;
            }
            exception = attempt.error;
            // Polling streams are long-lived subscriptions; giving up on a
            // non-retryable error would silently end the feed for the rest of the
            // page session, so they reconnect through every error class. A response
            // the transport marked terminal is the one exception: the subscription
            // itself has ended, so reconnecting would only resume stale work.
            if (!attempt.shouldRetry && (!shouldPoll || attempt.terminal)) {
                break;
            }
            if (shouldPoll) {
                logger.warn('SSE poll stream disconnected; reconnecting', {
                    normalizedPath,
                    errorCode: attempt.error.code,
                    tryNumber,
                });
            }
            if (shouldPoll && attempt.opened) {
                tryNumber = 0;
            }
            retryAfter = attempt.retryAfter;
            sleepTime = _getBackoffTime(backOffTime, tryNumber, retryAfter);
            ++tryNumber;
        } while (tryNumber <= numRetries);
        if (exception instanceof Error && (exception.cause?.code ?? 0) <= 0) {
            logger.error('SSE error', exception, {
                normalizedPath,
            });
        }
        if (exception) {
            throw exception;
        }
    })();
    return Object.assign(streamPromise, { close });
}
export function getNormalizedPath(response) {
    return NORMALIZED_PATHS.get(response);
}
//# sourceMappingURL=core.js.map